import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ArrowLeft, CreditCard, AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { getPaymentsOptions } from '@/lib/client/@tanstack/react-query.gen'

export const Route = createFileRoute('/link/$parentId/payments/')({
  component: PaymentsHistory,
})

function PaymentsHistory() {
  const { parentId } = Route.useParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')

  const { data: payments, isLoading } = useQuery({
    ...getPaymentsOptions({
      path: { parentId },
    }),
  })

  const filteredPayments = payments?.filter((payment: any) => {
    const matchesSearch = payment.id.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'ALL' || payment.status === statusFilter
    return matchesSearch && matchesStatus
  }) || []

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'SUCCEEDED': return 'Réussi'
      case 'PENDING': return 'En attente'
      case 'PROCESSING': return 'En cours'
      case 'FAILED': return 'Échoué'
      case 'REFUNDED': return 'Remboursé'
      case 'CANCELLED': return 'Annulé'
      default: return status
    }
  }

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'SUCCEEDED': return 'default'
      case 'REFUNDED': return 'secondary'
      case 'PENDING':
      case 'PROCESSING': return 'outline'
      case 'FAILED':
      case 'CANCELLED': return 'destructive'
      default: return 'outline'
    }
  }

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'CARD': return 'Carte bancaire'
      case 'SEPA': return 'Prélèvement SEPA'
      case 'BANK_TRANSFER': return 'Virement bancaire'
      case 'MANUAL': return 'Paiement manuel'
      default: return method
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/link/$parentId" params={{ parentId }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au tableau de bord
            </Link>
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Historique des paiements</h1>
          <p className="text-gray-600 mt-2">Consultez tous vos paiements (réussis, échoués, remboursés)</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Rechercher par ID de paiement..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tous les statuts</SelectItem>
                    <SelectItem value="SUCCEEDED">Réussi</SelectItem>
                    <SelectItem value="PENDING">En attente</SelectItem>
                    <SelectItem value="PROCESSING">En cours</SelectItem>
                    <SelectItem value="FAILED">Échoué</SelectItem>
                    <SelectItem value="REFUNDED">Remboursé</SelectItem>
                    <SelectItem value="CANCELLED">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{filteredPayments.length} paiement(s)</CardTitle>
            <CardDescription>Historique complet de vos paiements</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucun paiement trouvé</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPayments.map((payment: any) => (
                  <div
                    key={payment.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start space-x-4">
                        <CreditCard className="h-5 w-5 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">
                              {new Date(payment.createdAt).toLocaleDateString('fr-FR')}
                            </h3>
                            <Badge variant={getStatusVariant(payment.status)}>
                              {getStatusLabel(payment.status)}
                            </Badge>
                            {payment.refunded && payment.refundAmount > 0 && (
                              <Badge variant="secondary">
                                Remboursé : {payment.refundAmount.toFixed(2)}€
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {getMethodLabel(payment.paymentMethod)} via {payment.paymentProvider}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            ID: {payment.id.substring(0, 8)}...
                          </p>
                          {payment.failureMessage && (
                            <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                              <AlertTriangle className="h-4 w-4" />
                              <span>{payment.failureMessage}</span>
                            </div>
                          )}
                          {payment.comment && (
                            <p className="text-sm text-gray-600 mt-2 italic">
                              {payment.comment}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col md:items-end gap-2">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            {payment.amount.toFixed(2)}€
                          </p>
                          {payment.refunded && payment.refundDate && (
                            <p className="text-xs text-gray-500">
                              Remboursé le {new Date(payment.refundDate).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </div>

                        <Button asChild size="sm" variant="outline">
                          <Link
                            to="/link/$parentId/payments/$paymentId"
                            params={{ parentId, paymentId: payment.id }}
                          >
                            Voir détails
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
